import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {useMutation, useQuery} from "@tanstack/react-query";
import moment from "moment";
import {track} from "analytics";
import {createPaymentSheet} from "api";
import {calculateBookingPrice, getUserPendingMethods} from "api/booking.api";
import {STRIPE_PUBLISHABLE_KEY} from "conts/features";
import {useAuth} from "context/AuthContext";
import {useSelectedTimings} from "context/TimingContext";
import {DatesList} from "pages/user/showMoreDates/sections/DatesList";
import {userRoute} from "pages/user/user.routes";
import {Modal} from "ui/base/modal/Modal";
import {Alert, AlertIcon, Avatar, CheckIcon, IconBadge, PillButton, PillButtonOutline, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {getAvatarUrl, USDollar} from "utils";
import {mediaSizes} from "ui/theme";
import {AccountCheckout} from "./AccountCheckout";
import {GuestCheckout} from "./GuestCheckout";
import {PriceBreakdown} from "./PriceBreakdown";

// Stripe.js for the popup. With a publishable key in the build, new students
// can enter their card before they have an account; otherwise the key comes
// from the signed-in payment sheet (existing behavior).
const envStripe = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const ZERO_COST = {classCost: 0, discount: 0, tax: 0, totalCost: 0, discountPolicy: ""};

/**
 * Booking popup for the instructor page. Steps: choose times (existing date
 * picker), checkout (guest or signed-in), phone code (guests), request sent.
 * Booking rules are unchanged: the request goes through create-booking.
 */
export const BookingPopup = ({show, onClose, classDetail, openRequest}) => {
    const navigate = useNavigate();
    const {token} = useAuth();
    const {selectedTimings, numberOfStudents, typeOfClass, handleRemoveSelectedTiming, clearStore} = useSelectedTimings();
    const teacher = classDetail?.teacher;
    const teacherId = teacher?.id;
    const instructorName = classDetail?.first_name || "your instructor";

    const [datesOpen, setDatesOpen] = useState(false);
    // select -> checkout -> (verify, guests) -> done
    const [step, setStep] = useState("select");
    const [lessonsCost, setLessonsCost] = useState(ZERO_COST);
    const [priceError, setPriceError] = useState("");
    const [booked, setBooked] = useState(null);
    // A guest who is mid-way (code sent / just verified) keeps the guest
    // checkout until their request finishes, even after sign-in.
    const [guestFlow, setGuestFlow] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");

    // The chat launcher would sit on top of the times and buttons on phones.
    useEffect(() => {
        if (!show || !window.Intercom) return undefined;
        window.Intercom("update", {hide_default_launcher: true});
        return () => window.Intercom?.("update", {hide_default_launcher: false});
    }, [show]);

    // Each open: expand dates when asked, or when nothing is chosen yet.
    useEffect(() => {
        if (!openRequest?.n) return;
        if (openRequest.showDates || selectedTimings.length === 0) setDatesOpen(true);
        if (step === "done") {
            setStep("select");
            setBooked(null);
        }
        track("booking_popup_opened", {teacher_id: teacherId, show_dates: Boolean(openRequest.showDates), signed_in: Boolean(token)});
    }, [openRequest?.n]);

    // Nothing selected: back to choosing times.
    useEffect(() => {
        if (selectedTimings.length === 0 && (step === "checkout")) {
            setStep("select");
            setDatesOpen(true);
        }
    }, [selectedTimings.length, step]);

    const {mutate: getPrice, isLoading: isPricing} = useMutation({
        mutationFn: calculateBookingPrice,
        onError: () => setPriceError("We couldn't load the price. Please try again."),
        onSuccess: ({data}) => {
            setPriceError("");
            setLessonsCost({classCost: data.booking_total, discount: data.discount_amount, tax: data.tax, totalCost: data.total_cost, discountPolicy: data.discount_policy || ""});
        },
    });

    // Price for the current selection (re-priced after sign-in).
    useEffect(() => {
        if (!teacherId || !selectedTimings.length) {
            setLessonsCost(ZERO_COST);
            return;
        }
        getPrice({
            online_type: selectedTimings[0]?.type?.toLowerCase(),
            class_type: typeOfClass.value.toLowerCase(),
            no_of_class: selectedTimings.length,
            no_of_users: numberOfStudents.value,
            teacher_id: teacherId,
        });
    }, [teacherId, selectedTimings.length, numberOfStudents.value, typeOfClass.value, token]);

    // Existing rule: one pending request per instructor.
    const {data: pending} = useQuery({
        queryKey: ["getUserPendingMethods", teacherId, token],
        queryFn: () => getUserPendingMethods(teacherId),
        enabled: !!teacherId && !!token && step !== "done",
    });
    const hasPending = Boolean(pending?.data);

    // Signed-in students without a build-time key use the payment sheet's key.
    const {data: paymentSheet} = useQuery({
        queryKey: ["createPaymentSheet", token],
        queryFn: createPaymentSheet,
        enabled: !!token && !envStripe,
        refetchOnWindowFocus: false,
    });
    const [sheetStripe, setSheetStripe] = useState(null);
    useEffect(() => {
        if (!envStripe && paymentSheet?.publishableKey && !sheetStripe) setSheetStripe(loadStripe(paymentSheet.publishableKey));
    }, [paymentSheet, sheetStripe]);
    const stripePromise = envStripe || sheetStripe;

    const onlineType = selectedTimings?.[0]?.type?.toLowerCase() === "online" ? "online" : "offline";
    const bookingInput = useMemo(() => ({
        timing: selectedTimings,
        lessonsCost,
        classId: teacher?.subject_want_to_teach,
        typeOfClass,
        numberOfStudents,
        teacherId,
        onlineType,
    }), [selectedTimings, lessonsCost, teacher, typeOfClass, numberOfStudents, teacherId, onlineType]);

    const startCheckout = () => {
        setDatesOpen(false);
        setStep("checkout");
        track("booking_select_payment", {teacher_id: teacherId, classes: selectedTimings.length, signed_in: Boolean(token)});
    };

    const onBooked = (booking) => {
        setGuestFlow(false);
        setCheckoutError("");
        setBooked({timings: selectedTimings, total: lessonsCost.totalCost, students: numberOfStudents.label, id: booking?.id});
        setStep("done");
        track("booking_requested", {teacher_id: teacherId, booking_id: booking?.id, value: lessonsCost.totalCost});
    };

    const close = () => {
        if (step === "done") {
            clearStore();
            setStep("select");
            setBooked(null);
        }
        onClose();
    };

    const priceReady = selectedTimings.length > 0 && lessonsCost.totalCost > 0 && !isPricing;
    const showGuestCheckout = !token || guestFlow;

    return (
        <Modal show={show} handleClose={close} maxWidth={560} minHeight={0} mobileFull>
            <Body>
                <Header>
                    <Avatar src={getAvatarUrl(teacher?.profile_pic)} name={instructorName} size={48}/>
                    <div>
                        <Title>{step === "done" ? "Request sent" : `Book with ${instructorName}`}</Title>
                        <Subtitle>{teacher?.subject_sub_category || teacher?.class?.title}</Subtitle>
                    </div>
                </Header>

                {step === "done" ? (
                    <Done booked={booked} instructorName={instructorName}
                          onViewClasses={() => { close(); navigate(userRoute.myClasses, {state: "class_requests"}); }}
                          onDone={close}/>
                ) : (
                    <>
                        <Summary
                            timings={selectedTimings}
                            students={numberOfStudents?.label}
                            cost={lessonsCost}
                            isPricing={isPricing}
                            editable={step === "select" || step === "checkout"}
                            onRemove={handleRemoveSelectedTiming}
                        />
                        {priceError && <Alert role="alert"><AlertIcon/><span>{priceError}</span></Alert>}

                        {step !== "verify" && (
                            <DatesToggle type="button" onClick={() => setDatesOpen((o) => !o)} aria-expanded={datesOpen}>
                                {datesOpen ? "Hide dates" : selectedTimings.length ? "Show more dates" : "Choose a date and time"}
                                <Chevron $open={datesOpen}/>
                            </DatesToggle>
                        )}
                        {datesOpen && step !== "verify" && (
                            <DatesSection>
                                <DatesList classDetail={classDetail} initialTypeOfClass={typeOfClass} compact/>
                            </DatesSection>
                        )}

                        {step === "select" && selectedTimings.length > 0 && (
                            hasPending ? (
                                <Alert $tone="warning" role="alert">
                                    <AlertIcon/>
                                    <span>{pending?.message || `You already have a request waiting for ${instructorName}'s reply.`}</span>
                                </Alert>
                            ) : (
                                <PillButton type="button" $block onClick={startCheckout} disabled={!priceReady}>
                                    {isPricing ? <><Spinner $light $size={16}/> Updating price…</> : "Select Payment"}
                                </PillButton>
                            )
                        )}

                        {(step === "checkout" || step === "verify") && (
                            <Elements stripe={stripePromise}>
                                {!showGuestCheckout ? (
                                    hasPending ? (
                                        <Alert $tone="warning" role="alert">
                                            <AlertIcon/>
                                            <span>{pending?.message || `You already have a request waiting for ${instructorName}'s reply.`}</span>
                                        </Alert>
                                    ) : (
                                        <AccountCheckout
                                            bookingInput={bookingInput}
                                            cancellation={teacher?.cancellation}
                                            instructorName={instructorName}
                                            disabled={!priceReady}
                                            initialError={checkoutError}
                                            onBooked={onBooked}
                                        />
                                    )
                                ) : (
                                    <GuestCheckout
                                        teacherId={teacherId}
                                        bookingInput={bookingInput}
                                        cancellation={teacher?.cancellation}
                                        instructorName={instructorName}
                                        disabled={!priceReady}
                                        collectCard={Boolean(envStripe)}
                                        onCodeSent={() => { setGuestFlow(true); setStep("verify"); }}
                                        onBackToForm={() => { setGuestFlow(false); setStep("checkout"); }}
                                        onBooked={onBooked}
                                        onSignedInButNotBooked={(message) => {
                                            // Signed in, but the card step failed: continue as a signed-in student.
                                            setGuestFlow(false);
                                            setCheckoutError(message);
                                            setStep("checkout");
                                        }}
                                    />
                                )}
                            </Elements>
                        )}
                    </>
                )}
            </Body>
        </Modal>
    );
};

const Summary = ({timings, students, cost, isPricing, editable, onRemove}) => {
    if (!timings.length) {
        return <EmptySummary>Pick a time below to see the price.</EmptySummary>;
    }
    const single = timings.length === 1;
    const price = (
        <Total>
            {isPricing ? <Spinner $size={14}/> : (
                <>
                    {USDollar.format(cost?.totalCost || 0)}
                    {cost?.totalCost > 0 && <PriceBreakdown cost={cost} classes={timings.length} students={students}/>}
                </>
            )}
        </Total>
    );
    return (
        <SummaryCard aria-label="Booking summary">
            {timings.map(({id, date, time, type}) => {
                const extra = [type, single ? students : null].filter(Boolean).join(" · ");
                return (
                    <SummaryRow key={`${id}-${date}`}>
                        <Main><strong>{moment(date).format("ddd, MMM D")}</strong> ·&nbsp;{time}</Main>
                        {extra ? <Extra><Dot aria-hidden="true">·&nbsp;</Dot>{extra}</Extra> : null}
                        {single ? <PriceCell>{price}</PriceCell> : null}
                        {editable && (
                            <RemoveButton type="button" aria-label={`Remove ${moment(date).format("MMM D")} ${time}`}
                                          onClick={() => onRemove({id, date})}>×</RemoveButton>
                        )}
                    </SummaryRow>
                );
            })}
            {!single && (
                <TotalRow>
                    <Muted>{timings.length} classes · {students}</Muted>
                    {price}
                </TotalRow>
            )}
        </SummaryCard>
    );
};

const Done = ({booked, instructorName, onViewClasses, onDone}) => (
    <DoneWrap role="status">
        <IconBadge><CheckIcon size={30} strokeWidth={2.5}/></IconBadge>
        <DoneTitle>Your request was sent to {instructorName}</DoneTitle>
        <DoneText>
            It's pending until {instructorName} accepts. We placed a temporary hold on your card; you're only charged if the
            class is accepted. You'll get an email when {instructorName} replies.
        </DoneText>
        {booked?.timings?.length ? (
            <DoneSummary>
                {booked.timings.map(({id, date, time}) => (
                    <SummaryRow key={`${id}-${date}`}>
                        <Main><strong>{moment(date).format("ddd, MMM D")}</strong> ·&nbsp;{time}</Main>
                    </SummaryRow>
                ))}
                <TotalRow>
                    <Muted>{booked.students} · Pending approval</Muted>
                    <Total>{USDollar.format(booked.total || 0)}</Total>
                </TotalRow>
            </DoneSummary>
        ) : null}
        <DoneButtons>
            <PillButton type="button" $block onClick={onDone}>Done</PillButton>
            <PillButtonOutline type="button" onClick={onViewClasses}>View my classes</PillButtonOutline>
        </DoneButtons>
    </DoneWrap>
);

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    font-family: var(--font-primary);
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 36px; /* close button */
`;

const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.25;
    color: ${tokens.ink};
`;

const Subtitle = styled.p`
    margin: 2px 0 0;
    font-size: 14px;
    color: ${tokens.muted};
`;

const EmptySummary = styled.p`
    margin: 0;
    padding: 14px 16px;
    border-radius: 12px;
    background: ${tokens.warm};
    font-size: 14px;
    color: ${tokens.muted};
`;

const SummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    background: ${tokens.warm};
    font-size: 15px;
    line-height: 1.4;
    color: ${tokens.text};
    text-align: left;
`;

const DoneSummary = styled(SummaryCard)`
    align-self: stretch;
`;

/* Phones: "date · time  ×" over "type · students  price".
   Wider popups: one line, price on the right. */
const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    grid-template-areas:
        "main main remove"
        "extra price price";
    align-items: center;
    column-gap: 8px;
    row-gap: 2px;
    min-height: 28px;

    @media (width >= ${mediaSizes.md}) {
        display: flex;
        gap: 0;
    }
`;

const Main = styled.span`
    grid-area: main;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    strong {
        font-weight: 600;
        color: ${tokens.ink};
    }
`;

const Muted = styled.span`
    color: ${tokens.muted};
`;

const Extra = styled.span`
    grid-area: extra;
    color: ${tokens.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (width >= ${mediaSizes.md}) {
        margin-left: 0.35em;
        flex: 1;
    }
`;

const Dot = styled.span`
    display: none;

    @media (width >= ${mediaSizes.md}) {
        display: inline;
    }
`;

const PriceCell = styled.span`
    grid-area: price;
    justify-self: end;

    @media (width >= ${mediaSizes.md}) {
        margin-left: auto;
        padding-left: 12px;
    }
`;

const RemoveButton = styled.button`
    grid-area: remove;
    justify-self: end;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    margin-right: -6px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    color: ${tokens.muted};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background: #fff;
    }
`;

const TotalRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

/* Same size as the date; the ⓘ breakdown sits right next to it. */
const Total = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    color: ${tokens.ink};
`;

const DatesToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 4px 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.accent};
    cursor: pointer;
`;

const Chevron = styled.span`
    width: 8px;
    height: 8px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: ${({$open}) => ($open ? "rotate(-135deg) translate(-2px, -2px)" : "rotate(45deg)")};
    transition: transform 150ms ease;
`;

const DatesSection = styled.div`
    min-width: 0;
`;

const DoneWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;

    > div:last-child {
        width: 100%;
    }
`;

const DoneTitle = styled.h3`
    margin: 4px 0 0;
    font-size: 20px;
    font-weight: 600;
    color: ${tokens.ink};
`;

const DoneText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: ${tokens.text};
    max-width: 420px;
`;

const DoneButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-top: 16px;
`;
